import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Shield, Globe, CheckCircle } from 'lucide-react';
import SimpleLandingHero from '../components/landing/SimpleLandingHero';
import SimpleLeadCapture from '../components/landing/SimpleLeadCapture';
import LandingNav from '../components/landing/LandingNav';
import FooterSection from '../components/landing/FooterSection';

/**
 * LandingSeed — Phase 1 marketplace seed landing page
 *
 * Requirements (LUV-17):
 * - Clear headline communicating value proposition
 * - 3 key messages (Speed, Specialization, Certainty/Accessibility)
 * - Dual CTAs: "List your services" (vendors) + "Build your RFP" (SMBs)
 * - Lead capture form: email + company name + role
 * - No fancy design — clarity over polish
 * - EN/FR bilingual support
 * - Target conversion rate: 10%+ (Phase 1)
 */
const LandingSeed = () => {
  const { t } = useTranslation();
  const [activeForm, setActiveForm] = useState(null); // 'business' | 'vendor' | null

  const scrollToForm = (role) => {
    setActiveForm(role);
    document.getElementById('lead-capture')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{t('landing.meta.title') || 'ProposAI — The Canadian Procurement Marketplace'}</title>
        <meta name="description" content={t('landing.meta.description') || 'Connect businesses with qualified vendors. AI-powered RFP platform for Canadian procurement. Bilingual EN/FR, PIPEDA-compliant.'} />
        <link rel="canonical" href="https://proposai.ca/" />
      </Helmet>

      <LandingNav onLoginClick={() => {}} onRegisterClick={() => scrollToForm('business')} />

      {/* ── Hero with Dual CTAs ─────────────────────────────── */}
      <SimpleLandingHero
        onBusinessClick={() => scrollToForm('business')}
        onVendorClick={() => scrollToForm('vendor')}
      />

      {/* ── Trust Signals ───────────────────────────────────── */}
      <section className="py-8 px-4 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-700">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span>{t('landing.trust.stats.compliance') || 'PIPEDA Compliant'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              <span>{t('landing.trust.stats.bilingual') || 'Bilingual EN / FR'}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              <span>{t('landing.trust.stats.aiPowered') || 'AI-Powered'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Lead Capture Forms ──────────────────────────────── */}
      <section id="lead-capture" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              {t('landing.leadCapture.heading') || 'Join the marketplace'}
            </h2>
            <p className="text-slate-600">
              {t('landing.leadCapture.subheading') ||
               'No credit card required. Start for free.'}
            </p>
          </div>

          {/* Business Form */}
          {(!activeForm || activeForm === 'business') && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                {t('landing.leadCapture.businessTitle') || 'For Businesses'}
              </h3>
              <SimpleLeadCapture defaultRole="business" source="landing-business" />
            </div>
          )}

          {/* Vendor Form */}
          {(!activeForm || activeForm === 'vendor') && (
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                {t('landing.leadCapture.vendorTitle') || 'For Vendors'}
              </h3>
              <SimpleLeadCapture defaultRole="vendor" source="landing-vendor" />
            </div>
          )}
        </div>
      </section>

      {/* ── Simple FAQ ──────────────────────────────────────── */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
            {t('landing.faq.title') || 'Frequently Asked Questions'}
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">
                {t('landing.faq.q1') || 'Is this really free?'}
              </h3>
              <p className="text-slate-600 text-sm">
                {t('landing.faq.a1') ||
                 'Yes. No credit card required. Join the marketplace and start exploring opportunities.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">
                {t('landing.faq.q2') || 'How does AI matching work?'}
              </h3>
              <p className="text-slate-600 text-sm">
                {t('landing.faq.a2') ||
                 'Our AI analyzes vendor profiles and RFP requirements to suggest the best matches based on skills, experience, and availability.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">
                {t('landing.faq.q3') || 'Is my data secure?'}
              </h3>
              <p className="text-slate-600 text-sm">
                {t('landing.faq.a3') ||
                 'Absolutely. We are PIPEDA-compliant with AES-256 encryption and role-based access controls.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default LandingSeed;
